import type { AdminAppealPriority } from '@carcheck/shared';
import Chip, { type ChipProps } from '@mui/material/Chip';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Appeal priority badge chip for the moderation list + detail drawer
 * (AUT-130). Colours mirror the mockup's `badge--{neutral,warning,danger}`
 * classes via MUI Chip `color`. Labels resolve through i18n
 * (`appeals.priority.<value>`; CLAUDE.md rule #11).
 *
 * Typed as a total `Record<AdminAppealPriority, …>` so a priority-enum
 * change in `@carcheck/shared` fails typecheck here rather than rendering
 * an uncoloured chip at runtime.
 *
 * Mapping (mockup-aligned — the mockup labels `urgent` rows as «высокий»
 * red, `high` as «средний» amber; we keep distinct labels per enum value
 * and map urgent→error / high→warning / normal→default):
 *   - normal → default (neutral) "обычный"
 *   - high   → warning           "высокий"
 *   - urgent → error             "срочный"
 */
type ChipColor = ChipProps['color'];

const PRIORITY_COLOR: Record<AdminAppealPriority, ChipColor> = {
  normal: 'default',
  high: 'warning',
  urgent: 'error',
};

export const AppealPriorityBadge = ({
  priority,
}: {
  priority: AdminAppealPriority;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Chip size="small" color={PRIORITY_COLOR[priority]} label={t(`appeals.priority.${priority}`)} />
  );
};
